import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampaignLeadCountsSchema,
  CampaignSchema,
  CampaignSpendListSchema,
  LeadSchema,
  type CampaignDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { getData, getPage, patchJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { EmployeeName, ListSelect, Pager } from '../crm/common';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { CampaignDialog, schedule } from './CampaignsPage';
import {
  aed,
  campaignHistoryLabels,
  CAMPAIGNS,
  CountTable,
  LEADS,
  useCampaignLists,
  useLeadLists,
  valueLabel,
} from './common';
import { LeadTable } from './LeadsPage';

type DialogKind = 'edit' | 'status' | 'results';

/**
 * One campaign (`/marketing/campaigns/:id`; ERD §7.6, FRD-MKT-017 to MKT-026, MKT-028, MKT-035):
 * its plan, planned budget and recorded results, the status and results actions, the leads it
 * generated with their counts by status and source, its documents and history, and its actual
 * spend as Finance records it (CLR-18): posted supplier invoice lines naming the campaign.
 */
export function CampaignDetailPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const detail = useQuery({
    queryKey: ['marketing', 'campaign', id],
    queryFn: () => getData(`${CAMPAIGNS}/${id}`, CampaignSchema),
  });
  const back = (
    <Button component={RouterLink} to="/marketing/campaigns" startIcon={<ArrowBackIcon />}>
      All campaigns
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the campaign" />;
  if (detail.isError) {
    return <RecordFailure title="Campaign" noun="campaign" error={detail.error} back={back} />;
  }
  return (
    <CampaignRecord campaign={detail.data} back={back} created={params.get('created') === '1'} />
  );
}

function CampaignRecord({
  campaign,
  back,
  created,
}: {
  campaign: CampaignDto;
  back: ReactNode;
  created: boolean;
}) {
  const session = useSession();
  const lists = useCampaignLists();
  const canChange = hasCapability(session.data, 'marketing.campaign:change');
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const open = (kind: DialogKind) => (event: { currentTarget: HTMLElement }) => {
    event.currentTarget.focus();
    setDialog(kind);
  };
  const close = () => {
    setDialog(null);
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${campaign.code} — ${campaign.name}`}
        actions={
          <>
            {back}
            {canChange && (
              <Button variant="outlined" onClick={open('status')}>
                Change status
              </Button>
            )}
            {canChange && (
              <Button variant="outlined" onClick={open('results')}>
                Record results
              </Button>
            )}
            {canChange && (
              <Button variant="contained" onClick={open('edit')}>
                Edit
              </Button>
            )}
          </>
        }
      />
      {created && (
        <Alert severity="success" variant="outlined">
          Campaign {campaign.code} created. The Campaign ID was generated by the system.
        </Alert>
      )}
      <Section title="Campaign">
        <Facts
          label="Campaign details"
          facts={[
            ['Campaign ID', campaign.code],
            ['Name', campaign.name],
            ['Status', valueLabel(lists.data?.status, campaign.statusCode)],
            ['Objective', campaign.objective ?? '—'],
            ['Schedule', schedule(campaign)],
            ['Planned budget', aed(campaign.budget)],
            ['Actual spend (excl. VAT)', <CampaignSpend key="s" campaignId={campaign.id} />],
            ['Channel', valueLabel(lists.data?.channel, campaign.channelCode)],
            [
              'Target audience',
              valueLabel(lists.data?.targetAudience, campaign.targetAudienceCode),
            ],
            ['Owner', <EmployeeName key="o" id={campaign.ownerEmployeeId} />],
            ['Attribution model', campaign.attributionModel ?? '—'],
          ]}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, pb: 2, display: 'block' }}
        >
          The planned budget is planning information, not an accounting budget. Actual spend is
          Finance&apos;s figure: posted supplier invoice lines naming this campaign, excluding VAT,
          less posted credit notes of those lines.
        </Typography>
      </Section>
      <Section title="Results">
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {campaign.results ?? 'No results recorded yet.'}
          </Typography>
        </Box>
      </Section>
      {hasCapability(session.data, 'marketing.lead:view') && <CampaignLeads campaign={campaign} />}
      <DocumentsPanel
        recordType="marketing.campaign"
        recordId={campaign.id}
        canAttach={canChange}
      />
      <RecordHistory
        recordType="marketing.campaign"
        recordId={campaign.id}
        labels={campaignHistoryLabels(lists.data)}
      />

      {dialog === 'edit' && (
        <CampaignDialog
          key={`${campaign.id}:${String(campaign.version)}`}
          campaign={campaign}
          onClose={close}
        />
      )}
      {dialog === 'status' && <StatusDialog campaign={campaign} onClose={close} />}
      {dialog === 'results' && <ResultsDialog campaign={campaign} onClose={close} />}
    </Stack>
  );
}

function useCampaignChanged() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['marketing', 'campaign'] });
    await queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
    await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
  };
}

/**
 * Moves the campaign to another configured status (FRD-MKT-022), after a confirmation; the owner
 * is notified where a notification rule is configured (FRD-MKT-047).
 */
function StatusDialog({ campaign, onClose }: { campaign: CampaignDto; onClose: () => void }) {
  const session = useSession();
  const lists = useCampaignLists();
  const changed = useCampaignChanged();
  const [status, setStatus] = useState(campaign.statusCode);
  const [confirming, setConfirming] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${CAMPAIGNS}/${campaign.id}`,
        { statusCode: status },
        CampaignSchema,
        session.data?.csrfToken ?? '',
        campaign.version,
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  if (confirming) {
    return (
      <ConfirmDialog
        open
        title="Change campaign status"
        message={`Change ${campaign.code} from ${valueLabel(lists.data?.status, campaign.statusCode)} to ${valueLabel(lists.data?.status, status)}?`}
        confirmLabel="Change status"
        busy={save.isPending}
        error={save.isError ? save.error.message : null}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          save.mutate();
        }}
      />
    );
  }
  return (
    <FormDialog
      open
      title="Change campaign status"
      submitLabel="Continue"
      canSubmit={status !== campaign.statusCode}
      submitting={false}
      error={null}
      onClose={onClose}
      onSubmit={() => {
        setConfirming(true);
      }}
    >
      <ListSelect
        label="Status"
        required
        values={lists.data?.status}
        value={status}
        current={campaign.statusCode}
        onChange={setStatus}
      />
    </FormDialog>
  );
}

/** Records the campaign's results against it (FRD-MKT-019). */
function ResultsDialog({ campaign, onClose }: { campaign: CampaignDto; onClose: () => void }) {
  const session = useSession();
  const changed = useCampaignChanged();
  const [results, setResults] = useState(campaign.results ?? '');
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${CAMPAIGNS}/${campaign.id}`,
        { results: results.trim() === '' ? null : results.trim() },
        CampaignSchema,
        session.data?.csrfToken ?? '',
        campaign.version,
      ),
    onSuccess: async () => {
      await changed();
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Record results"
      submitLabel="Save results"
      canSubmit={results.trim() !== (campaign.results ?? '')}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Results"
        multiline
        minRows={4}
        value={results}
        onChange={(e) => {
          setResults(e.target.value);
        }}
      />
    </FormDialog>
  );
}

const LEAD_PAGE = 25;

/** The leads this campaign generated (FRD-MKT-028), with counts by status and by source. */
function CampaignLeads({ campaign }: { campaign: CampaignDto }) {
  const leadLists = useLeadLists();
  const [page, setPage] = useState(1);
  const counts = useQuery({
    queryKey: ['marketing', 'campaign', campaign.id, 'lead-counts'],
    queryFn: () => getData(`${CAMPAIGNS}/${campaign.id}/lead-counts`, CampaignLeadCountsSchema),
  });
  const leads = useQuery({
    queryKey: ['marketing', 'leads', 'campaign', campaign.id, page],
    queryFn: () =>
      getPage(
        `${LEADS}?page=${String(page)}&pageSize=${String(LEAD_PAGE)}&sort=code&filter[campaign_id]=${campaign.id}`,
        LeadSchema,
      ),
  });
  return (
    <Section title="Leads">
      {counts.isError && <ErrorState message={counts.error.message} />}
      {counts.data !== undefined && (
        <Box
          sx={{
            p: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle2">Totals</Typography>
            <Typography variant="body2">
              {counts.data.leads} lead(s), {counts.data.converted} converted to a client
            </Typography>
          </Stack>
          <CountTable
            label="Leads by status"
            heading="Status"
            rows={counts.data.byStatus}
            values={leadLists.data?.status}
          />
          <CountTable
            label="Leads by source"
            heading="Source"
            rows={counts.data.bySource}
            values={leadLists.data?.source}
          />
        </Box>
      )}
      {leads.isPending && <LoadingState label="Loading the campaign's leads" />}
      {leads.isError && <ErrorState message={leads.error.message} />}
      {leads.data?.items.length === 0 && <EmptyState title="No leads from this campaign yet" />}
      {leads.data !== undefined && leads.data.items.length > 0 && (
        <>
          <LeadTable leads={leads.data.items} />
          <Pager
            page={page}
            pageSize={LEAD_PAGE}
            total={leads.data.total}
            noun="lead(s)"
            onPage={setPage}
          />
        </>
      )}
    </Section>
  );
}

/**
 * The campaign's actual spend to date (CLR-18), read from Finance for a session that may view
 * supplier invoices, linked to the posted invoices behind it; nobody else is shown an amount.
 */
function CampaignSpend({ campaignId }: { campaignId: string }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'finance.supplier_invoice:view');
  const spend = useQuery({
    queryKey: ['finance', 'campaign-spend', campaignId],
    enabled: canView,
    queryFn: () =>
      getData(
        `/api/v1/finance/supplier-invoices/campaign-spend?campaignId=${encodeURIComponent(campaignId)}`,
        CampaignSpendListSchema,
      ),
  });
  if (!canView) return <>Needs permission to view supplier invoices</>;
  if (spend.isPending) return <>…</>;
  if (spend.isError) return <>Not available</>;
  const amount = spend.data.find((row) => row.campaignId === campaignId)?.amount ?? '0.00';
  return (
    <RecordLink
      to={`/supplier-invoices?campaign_id=${encodeURIComponent(campaignId)}&status=posted`}
    >
      {aed(amount)}
    </RecordLink>
  );
}
